
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ImageUploadResponse } from './types';

export const useUploader = () => {
  const { toast } = useToast();

  const uploadByFile = async (file: File): Promise<ImageUploadResponse> => {
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const fileExt = fileName.split('.').pop() || 'jpg';
      const filePath = `${crypto.randomUUID()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          contentType: file.type
        });
      
      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);
      
      return {
        success: 1,
        file: {
          url: publicUrl || '',
          filePath: filePath,
          mime: file.type,
          size: file.size,
          width: null,
          height: null
        }
      };
    } catch (error) {
      console.error('Image upload failed:', error);
      toast({
        title: "Upload Failed",
        description: "Could not upload image. Please try again.",
        variant: "destructive"
      });
      return {
        success: 0,
        file: {
          url: ''
        }
      };
    }
  };

  const uploadByUrl = async (url: string): Promise<ImageUploadResponse> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      const fileName = url.split('/').pop() || 'image.jpg';
      const file = new File([blob], fileName, { type: blob.type });
      
      // Use the uploadByFile function instead of referring to 'this'
      return await uploadByFile(file);
    } catch (error) {
      console.error('Image upload by URL failed:', error);
      toast({
        title: "Upload Failed",
        description: "Could not upload image from URL. Please try again.",
        variant: "destructive"
      });
      return {
        success: 0,
        file: {
          url: ''
        }
      };
    }
  };

  return {
    uploadByFile,
    uploadByUrl
  };
};
